import React, { useState, useEffect } from 'react';
import { Container } from '@material-ui/core';
import { ILine } from '../../global';
import { IProduct } from '../../Types';
import {
  lineStatus,
  sortByDate,
  isUserInInitialOnboarding,
  displayLineEditErrors,
  addOrDeleteLineEditError,
} from '../../utils';
import { useLinesContext } from '../../contexts/Lines';
import { useAuthContext } from '../../contexts/Auth';
import { useSnackbarContext } from '../../contexts/Snackbar';
import { useEventTrackerContext } from '../../contexts/EventTracker';
import { v4 as uuidv4 } from 'uuid';
import { useCompanyContext } from '../../contexts/Company';
import messages from '../../messages';
import cloneDeep from 'lodash.clonedeep';
import mapValues from 'lodash.mapvalues';
import compact from 'lodash.compact';
import makeStyles from '@material-ui/core/styles/makeStyles';
import LayoutProducts from './LayoutProducts';
import LayoutTimeline from './LayoutTimeline';
import Grid from '@material-ui/core/Grid';
import NewProductButton from './Buttons/NewProduct';
import FilterProduct from './Filters/FilterProduct';
import FilterStatus from './Filters/FilterStatus';
import EditLineContextProvider from '../../contexts/EditLine';

const useStyles = makeStyles((theme: any) => ({
  root: {
    position: 'relative',
    minHeight: '95%',
    marginTop: 0,
    maxWidth: 4000,
    width: '100%',
  },
  filtering: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  product: {
    flexDirection: 'row',
  },
  productsLayoutContainer: {
    minWidth: '275px',
    zIndex: theme.zIndex.pageContent + 2,
  },
  linesLayoutContainer: {
    maxWidth: 'calc(100% - 275px)',
    zIndex: theme.zIndex.pageContent + 1,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    minHeight: 104,
    paddingTop: theme.spacing(1),
    backgroundColor: 'white',
  },
  stickyHeaders: {
    position: 'sticky',
    top: 0,
    opacity: '100%',
    zIndex: theme.zIndex.pageContent + 1,
  },
}));

function getProductsToShow(products: Array<IProduct>, filterProduct: string) {
  products = sortByDate(products || [], 'createdAt', 'desc');
  const filteredProducts = products.filter((product) => {
    return filterProduct === null || product.id === filterProduct;
  });
  return filteredProducts;
}

function addRowNumber(linesGroupedByProduct: Array<any>) {
  const flattenedLines = linesGroupedByProduct.flat();
  flattenedLines.forEach((line, i) => {
    line['row'] = i + 1;
  });
}

function groupLinesByProduct(
  products: any,
  lines: Array<ILine>,
  hasFilterProduct: boolean,
) {
  const productsObj = {};
  products.forEach((product) => (productsObj[product.id] = []));
  lines.forEach((line) => {
    const { product } = line;
    if (productsObj[product]) {
      productsObj[product].push(line);
    } else if (!hasFilterProduct) {
      productsObj['null'] = productsObj['null'] || [];
      productsObj['null'].push(line);
    }
  });
  return productsObj;
}

function filterLineByStatus(
  line: ILine,
  filterStatus: string,
  hasSelectedCloneLine: boolean,
) {
  return !filterStatus || line.status === filterStatus || hasSelectedCloneLine;
}
function filterLineByPlan(line: ILine, filterPlan: string) {
  return !filterPlan || line.financing_application === filterPlan;
}

function filterLineByActivePlanEditing(
  line: ILine,
  selectedCloneLine: ILine,
  isEditingActivePlan: boolean,
  isAdminUser: boolean,
) {
  if (!isEditingActivePlan) {
    if (isAdminUser) {
      return true;
    }
    if (
      !line.origin_line ||
      line.status === lineStatus.active ||
      line.status === lineStatus.complete
    ) {
      return true;
    }
  }
  if (isEditingActivePlan) {
    let cloneApp = selectedCloneLine?.financing_application;
    if (line.financing_application === cloneApp) {
      return true;
    }
  }
  return false;
}

function filterLinesOfProduct(
  lines: Array<ILine>,
  isEditingActivePlan: boolean,
  selectedCloneLine: ILine,
  filterStatus: string,
  filterPlan: string,
  isAdminUser: boolean,
) {
  return lines
    .filter((line) =>
      filterLineByStatus(line, filterStatus, !!selectedCloneLine),
    )
    .filter((line) => filterLineByPlan(line, filterPlan))
    .filter((line) =>
      filterLineByActivePlanEditing(
        line,
        selectedCloneLine,
        isEditingActivePlan,
        isAdminUser,
      ),
    );
}

function shouldShowProductWhenFiltering(
  lines: Array<ILine>,
  productId: string,
  filterProduct: string,
) {
  return lines.length > 0 || (filterProduct && productId === filterProduct);
}

function generateEmptyLine(productId: string) {
  return {
    id: uuidv4(),
    empty: true,
    product: productId,
  };
}

function filterAndFormatLines(
  linesByProductId: { [key: string]: Array<ILine> },
  isEditingActivePlan: boolean,
  selectedCloneLine: ILine,
  filterStatus: string,
  filterProduct: string,
  filterPlan: string,
  isAdminUser: boolean,
) {
  const isCurrentlyFiltering = filterStatus || filterProduct || filterPlan;
  return mapValues(linesByProductId, (lines: Array<any>, prodId: string) => {
    lines = filterLinesOfProduct(
      lines,
      isEditingActivePlan,
      selectedCloneLine,
      filterStatus,
      filterPlan,
      isAdminUser,
    );
    lines = sortByDate(lines);
    if (isCurrentlyFiltering || isEditingActivePlan) {
      if (!shouldShowProductWhenFiltering(lines, prodId, filterProduct)) {
        return null;
      }
    }
    lines.push(generateEmptyLine(prodId));
    return lines;
  });
}

function groupLines(
  lines: Array<ILine>,
  products: Array<IProduct>,
  isEditingActivePlan: boolean,
  selectedCloneLine: ILine,
  filterStatus: string,
  filterProduct: string,
  filterPlan: string,
  isAdminUser: boolean,
) {
  const filteredProducts = getProductsToShow(products, filterProduct);
  const linesByProductId = groupLinesByProduct(
    filteredProducts,
    lines,
    !!filterProduct,
  );
  const linesToShowByProdId = filterAndFormatLines(
    linesByProductId,
    isEditingActivePlan,
    selectedCloneLine,
    filterStatus,
    filterProduct,
    filterPlan,
    isAdminUser,
  );
  const groupedLines = compact(Object.values(linesToShowByProdId));
  addRowNumber(groupedLines);
  return groupedLines;
}

export default function Content() {
  const classes = useStyles();
  const snackbar = useSnackbarContext();
  const { trackEvent } = useEventTrackerContext();
  const {
    products,
    lines,
    selectedCloneLine,
    lineOnEditMode,
    setIsLineSelected,
    editLineSubmit,
    editActiveLineSave,
    setLoading,
  } = useLinesContext();
  const { isAdminUser } = useAuthContext();
  const { business } = useCompanyContext();
  const [filterProduct, setFilterProduct] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterPlan, setFilterPlan] = useState(null);
  const isEditingActivePlan = lines.find((line) => line.editActiveLine);

  const userInInitialOnboarding = isUserInInitialOnboarding(products, lines);

  const linesGroupedByProduct = groupLines(
    lines,
    products,
    isEditingActivePlan ? true : false,
    selectedCloneLine,
    filterStatus,
    filterProduct,
    filterPlan,
    isAdminUser,
  );

  useEffect(() => {
    const isFiltering = filterStatus || filterProduct;
    if (linesGroupedByProduct.length === 0 && isFiltering) {
      snackbar.show(messages.lines.noLinesMatchingFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterProduct]);

  useEffect(() => {
    const selectedLines = lines.filter((line) => line['checked']);
    selectedLines.length > 0
      ? setIsLineSelected(true)
      : setIsLineSelected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines]);

  let updatedLineInfo = {};
  let updatedLineErrors = {};

  function setLineInfoDuringEdit(key: string, data: string | number) {
    updatedLineInfo[key] = data;
  }

  function addOrDeleteError(
    addError: boolean = true,
    errKey: string,
    error?: string,
  ) {
    addOrDeleteLineEditError(updatedLineErrors, addError, errKey, error);
  }

  async function handleEditLineSubmit(line: ILine) {
    const lineCopy = cloneDeep(line);
    const errorDisplayed = displayLineEditErrors(
      lineCopy,
      updatedLineErrors,
      snackbar,
    );
    if (!errorDisplayed) {
      const updatedLineInfoCopy = cloneDeep(updatedLineInfo);
      const updatedLine = { ...lineCopy, ...updatedLineInfoCopy };
      if (lineCopy.editActiveLine) {
        return editActiveLineSave(updatedLine);
      }
      setLoading(true);
      try {
        const isNewLine = line.new;
        const lineId = await editLineSubmit(updatedLine);
        const successMessage = isNewLine
          ? messages.lines.createLineSuccess
          : messages.lines.success(lineCopy.name, 'edited');
        snackbar.show(successMessage, 'success');
        trackEvent({
          event: line.new ? 'line_added' : 'line_edited',
          total_lines: lines.length,
          line: { ...updatedLine, id: lineId },
        });
      } catch (error) {
        let errorMessage = '';
        if (error?.message === 'INVALID_LINE_LENGTH') {
          errorMessage = messages.lines.invalidLineLength;
        }
        if (error?.message === 'LINE_NAME_NOT_UNIQUE') {
          errorMessage = messages.lines.lineEditErrors.nameNotUnique;
        }
        if (!errorMessage) {
          errorMessage =
            messages.lines[line?.new ? 'createLineError' : 'editLineError'];
        }
        snackbar.show(errorMessage, 'error', 10000);
      }
      setLoading(false);
    }
  }

  return (
    <Container className={classes.root}>
      <Grid id="LinesGridContainer" container spacing={2} direction="row">
        <EditLineContextProvider>
          <Grid item xs={2} className={classes.productsLayoutContainer}>
            <Grid
              container
              spacing={2}
              direction="row"
              className={classes.stickyHeaders}
            >
              <div className={classes.header}>
                {(!userInInitialOnboarding || business?.leadId) && (
                  <>
                    <div>
                      <NewProductButton lineOnEditMode={lineOnEditMode} />
                    </div>
                    <div className={classes.filtering}>
                      <FilterProduct
                        onChange={(value) => setFilterProduct(value)}
                        lineOnEditMode={lineOnEditMode}
                      />
                      <FilterStatus
                        onChange={(value) =>
                          setFilterStatus(value === 'all' ? null : value)
                        }
                        lineOnEditMode={lineOnEditMode}
                      />
                    </div>
                  </>
                )}
              </div>
            </Grid>
            <div>
              {linesGroupedByProduct && (
                <LayoutProducts
                  linesGroupedByProduct={linesGroupedByProduct}
                  products={products}
                  isLineBeingEdited={lineOnEditMode}
                  setLineInfoDuringEdit={setLineInfoDuringEdit}
                  addOrDeleteError={addOrDeleteError}
                  handleLineSubmit={handleEditLineSubmit}
                  lineOnEditMode={lineOnEditMode}
                />
              )}
            </div>
          </Grid>
          <Grid item xs={10} className={classes.linesLayoutContainer}>
            {linesGroupedByProduct && (
              <LayoutTimeline
                linesGroupedByProduct={linesGroupedByProduct}
                numOfLinesToShow={linesGroupedByProduct.flat().length}
                handleLineSubmit={handleEditLineSubmit}
                addOrDeleteError={addOrDeleteError}
                filterPlan={filterPlan}
                setFilterPlan={setFilterPlan}
              />
            )}
          </Grid>
        </EditLineContextProvider>
      </Grid>
    </Container>
  );
}
